import {useEffect, useRef, useState, useMemo} from 'react'
import {useSearchParams, useNavigate} from '@github-ui/use-navigate'
import {TextInput, FormControl} from '@primer/react'

import {SearchIcon} from '@primer/octicons-react'
import {debounce} from '@github/mini-throttle'
import type {BranchPageType} from '../types'
import useBranchTabPath from '../hooks/use-branch-tab-path'

type SearchInputProps = {
  onChange?: (newValue: string) => void
  selectedPage: BranchPageType
}

export default function SearchInput({onChange, selectedPage}: SearchInputProps) {
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchFocused, setSearchFocused] = useState(false)
  const [filter, setFilter] = useState(searchParams.get('query') ?? '')
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const getBranchTabPath = useBranchTabPath()

  // Store the full search handler in a single ref so the stable debounced function always calls the latest logic
  const handleSearchRef = useRef((_newValue: string) => {})
  useEffect(() => {
    handleSearchRef.current = (newValue: string) => {
      onChange?.(newValue)
      if (selectedPage !== 'all') {
        navigate(
          `${getBranchTabPath('all')}?query=${encodeURIComponent(newValue)}&lastTab=${encodeURIComponent(
            selectedPage,
          )}`,
          {replace: true},
        )
      } else if (searchParams.get('lastTab') && !newValue) {
        navigate(`${getBranchTabPath(searchParams.get('lastTab') as BranchPageType)}?query=`, {replace: true})
      } else {
        setSearchParams({query: newValue}, {replace: true})
      }
    }
  })

  const debouncedOnChange = useMemo(
    () =>
      // eslint-disable-next-line react-hooks/refs -- ref is read inside the debounced callback (event handler), not during render
      debounce((newValue: string) => handleSearchRef.current(newValue), 250),
    [],
  )

  useEffect(() => {
    return () => debouncedOnChange.cancel()
  }, [debouncedOnChange])

  return (
    <FormControl>
      <FormControl.Label visuallyHidden>Search</FormControl.Label>
      <TextInput
        value={filter}
        ref={inputRef}
        placeholder="Search branches..."
        leadingVisual={SearchIcon}
        onChange={event => {
          setFilter(event.target.value)
          debouncedOnChange(event.target.value)
        }}
        block
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        data-react-autofocus={searchFocused}
      />
    </FormControl>
  )
}
